/**
 * @fileoverview Shared Mermaid renderer. Used by the inline chat DiagramCard,
 * the ```mermaid fence in chat prose, and the canvas DiagramNode so all three
 * render identically.
 *
 * Renders are debounced so streamed/typed source doesn't thrash the parser,
 * and invalid source degrades to the error message plus the raw code (never a
 * blank box). Re-renders when the app theme flips so diagrams follow
 * light/dark mode.
 */
import { useEffect, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import { cn } from "@/utils/misc";
import { isDarkMode, renderMermaidSvg } from "./mermaid";
import { DiagramLightbox } from "./DiagramLightbox";

type RenderState =
  | { status: "loading" }
  | { status: "ok"; svg: string }
  | { status: "error"; message: string };

/** Watches the <html> class list so diagrams re-theme with the app. */
function useIsDark(): boolean {
  const [dark, setDark] = useState(isDarkMode());
  useEffect(() => {
    const observer = new MutationObserver(() => setDark(isDarkMode()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

export function DiagramRenderer({
  code,
  title,
  className,
  debounceMs = 250,
  expandable = true,
}: {
  code: string;
  /** Shown in the full-screen viewer header. */
  title?: string;
  className?: string;
  /** Delay before (re)parsing — keeps streaming/typing smooth. */
  debounceMs?: number;
  /** Click / expand button opens a zoomable full-screen viewer. */
  expandable?: boolean;
}) {
  const [state, setState] = useState<RenderState>({ status: "loading" });
  const [expanded, setExpanded] = useState(false);
  const dark = useIsDark();
  const latestRender = useRef(0);

  useEffect(() => {
    const renderId = ++latestRender.current;
    const timer = setTimeout(() => {
      void renderMermaidSvg(code, { theme: dark ? "dark" : "default" }).then(
        (result) => {
          if (latestRender.current !== renderId) return; // stale
          setState(
            "svg" in result
              ? { status: "ok", svg: result.svg }
              : { status: "error", message: result.error },
          );
        },
      );
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [code, dark, debounceMs]);

  if (state.status === "loading") {
    return (
      <div
        className={cn(
          "flex h-32 animate-pulse items-center justify-center rounded-md bg-muted/50 text-xs text-muted-foreground",
          className,
        )}
      >
        Rendering diagram…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className={cn("space-y-2", className)}>
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          Diagram could not be rendered: {state.message}
        </p>
        <pre className="overflow-x-auto rounded-lg bg-muted p-3 font-mono text-xs">
          {code}
        </pre>
      </div>
    );
  }

  if (!expandable) {
    return (
      <div
        className={cn(
          "overflow-x-auto py-1 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full",
          className,
        )}
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- SVG produced by renderMermaidSvg (mermaid.ts) with securityLevel "strict" and htmlLabels off: labels are HTML-encoded, scripts stripped
        dangerouslySetInnerHTML={{ __html: state.svg }}
      />
    );
  }

  return (
    <div className={cn("group relative", className)}>
      <button
        type="button"
        title="Click to expand"
        aria-label="Expand diagram"
        onClick={() => setExpanded(true)}
        className="block w-full cursor-zoom-in overflow-x-auto rounded-md py-1 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full"
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- SVG produced by renderMermaidSvg (mermaid.ts) with securityLevel "strict" and htmlLabels off: labels are HTML-encoded, scripts stripped
        dangerouslySetInnerHTML={{ __html: state.svg }}
      />
      <button
        type="button"
        title="Expand diagram"
        onClick={() => setExpanded(true)}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-border bg-background/90 px-2 py-1 text-xs text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-foreground focus:opacity-100 group-hover:opacity-100"
      >
        <Maximize2 className="h-3 w-3" />
        Expand
      </button>
      <DiagramLightbox
        svg={state.svg}
        title={title}
        open={expanded}
        onOpenChange={setExpanded}
      />
    </div>
  );
}
